import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AgentMessage } from '@/lib/agent/mcp-client';

/**
 * Navigation action triggered by agent commands
 */
export interface AgentNavigationAction {
  path: string;
  highlightIds?: string[];
  highlightType?: 'quote' | 'stream' | 'video';
}

interface AgentState {
  // Sidebar State
  isSidebarOpen: boolean;

  // Chat State
  messages: AgentMessage[];
  isConnected: boolean;
  isLoading: boolean;

  // Navigation/Highlight State
  pendingNavigation: AgentNavigationAction | null;
  highlightedItems: Set<string>;
}

interface AgentActions {
  // Sidebar Actions
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;

  // Chat Actions
  addMessage: (message: AgentMessage) => void;
  setMessages: (messages: AgentMessage[]) => void;
  clearMessages: () => void;
  setIsConnected: (connected: boolean) => void;
  setIsLoading: (loading: boolean) => void;

  // Navigation Actions
  setPendingNavigation: (action: AgentNavigationAction | null) => void;
  clearPendingNavigation: () => void;
  addHighlightedItem: (id: string) => void;
  removeHighlightedItem: (id: string) => void;
  clearHighlightedItems: () => void;
}

export const useAgentStore = create<AgentState & AgentActions>()(
  devtools(
    persist(
      (set) => ({
        // Initial State
        isSidebarOpen: false,
        messages: [],
        isConnected: false,
        isLoading: false,
        pendingNavigation: null,
        highlightedItems: new Set<string>(),

        // Sidebar Actions
        openSidebar: () => set({ isSidebarOpen: true }),
        closeSidebar: () => set({ isSidebarOpen: false }),
        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

        // Chat Actions
        addMessage: (message) => set((state) => ({
          messages: [...state.messages, message]
        })),
        setMessages: (messages) => set({ messages }),
        clearMessages: () => set({ messages: [] }),
        setIsConnected: (connected) => set({ isConnected: connected }),
        setIsLoading: (loading) => set({ isLoading: loading }),

        // Navigation Actions
        setPendingNavigation: (action) => set({ pendingNavigation: action }),
        clearPendingNavigation: () => set({ pendingNavigation: null }),
        addHighlightedItem: (id) => set((state) => {
          const newSet = new Set(state.highlightedItems);
          newSet.add(id);
          return { highlightedItems: newSet };
        }),
        removeHighlightedItem: (id) => set((state) => {
          const newSet = new Set(state.highlightedItems);
          newSet.delete(id);
          return { highlightedItems: newSet };
        }),
        clearHighlightedItems: () => set({ highlightedItems: new Set<string>() }),
      }),
      {
        name: 'agent-store',
        partialize: (state) => ({
          // Only persist messages (chat history)
          messages: state.messages,
        }),
      }
    ),
    {
      name: 'agent-store'
    }
  )
);
